import { labelToCol } from "./addr.js";
const UNQUOTED_SHEET_RE = /^([A-Za-z_][A-Za-z0-9_]*)!/;
const CELL_RANGE_RE = /^(\$?)([A-Za-z]+)(\$?)(\d+)(?::(\$?)([A-Za-z]+)(\$?)(\d+))?/;
function readCellRange(src) {
    const m = CELL_RANGE_RE.exec(src);
    if (!m?.[2] || !m[4])
        return null;
    const start = cellAddr(undefined, m[1], m[2], m[3], m[4]);
    const addr = m[6] && m[8] ? { start, end: cellAddr(undefined, m[5], m[6], m[7], m[8]) } : { start };
    return { addr, length: m[0].length };
}
export function readQuotedSheet(src, i) {
    if (src[i] !== "'")
        return null;
    let j = i + 1;
    let name = "";
    while (j < src.length) {
        if (src[j] === "'") {
            if (src[j + 1] === "'") {
                name += "'";
                j += 2;
                continue;
            }
            if (src[j + 1] !== "!" || name === "")
                return null;
            return { name, afterBang: j + 2 };
        }
        name += src[j];
        j++;
    }
    return null;
}
function cellAddr(sheet, colAbs, col, rowAbs, row) {
    const addr = { col: labelToCol(col), row: Number(row) - 1 };
    if (sheet)
        addr.sheet = sheet;
    if (colAbs)
        addr.colAbs = true;
    if (rowAbs)
        addr.rowAbs = true;
    return addr;
}
export function tokenize(src) {
    const tokens = [];
    let i = 0;
    while (i < src.length) {
        const c = src[i];
        if (c === " " || c === "\t") {
            i++;
            continue;
        }
        if (c === '"') {
            const start = i++;
            let value = "";
            while (i < src.length && src[i] !== '"')
                value += src[i++];
            if (src[i] === '"')
                i++;
            tokens.push({ kind: "str", span: { start, end: i }, value });
            continue;
        }
        if (c >= "0" && c <= "9") {
            const start = i;
            while (i < src.length && ((src[i] >= "0" && src[i] <= "9") || src[i] === "."))
                i++;
            tokens.push({ kind: "num", span: { start, end: i }, value: Number(src.slice(start, i)) });
            continue;
        }
        if (c === "(") {
            tokens.push({ kind: "lparen", span: { start: i, end: ++i } });
            continue;
        }
        if (c === ")") {
            tokens.push({ kind: "rparen", span: { start: i, end: ++i } });
            continue;
        }
        if (c === "+" || c === "-" || c === "*" || c === "/" || c === "^") {
            tokens.push({ kind: "op", span: { start: i, end: ++i }, value: c });
            continue;
        }
        if (c === "<" || c === ">" || c === "=") {
            const two = src.slice(i, i + 2);
            const op = two === "<=" || two === ">=" || two === "<>" ? two : c;
            tokens.push({ kind: "op", span: { start: i, end: i + op.length }, value: op });
            i += op.length;
            continue;
        }
        if (c === ",") {
            tokens.push({ kind: "comma", span: { start: i, end: ++i } });
            continue;
        }
        if (c === "#") {
            if (!src.startsWith("#REF!", i))
                throw new Error(`Unexpected character: ${c}`);
            tokens.push({ kind: "err", span: { start: i, end: i + 5 }, code: "#REF!" });
            i += 5;
            continue;
        }
        if (c === "'") {
            const quoted = readQuotedSheet(src, i);
            const cell = quoted ? readCellRange(src.slice(quoted.afterBang)) : null;
            if (!quoted || !cell)
                throw new Error("Unexpected character: '");
            const start = i;
            i = quoted.afterBang + cell.length;
            cell.addr.start.sheet = quoted.name;
            tokens.push({ kind: "ref", span: { start, end: i }, addr: cell.addr });
            continue;
        }
        if (c === "$" || /[A-Za-z_]/.test(c)) {
            const rest = src.slice(i);
            const sheetMatch = UNQUOTED_SHEET_RE.exec(rest);
            const cellAt = sheetMatch ? sheetMatch[0].length : 0;
            const cell = readCellRange(rest.slice(cellAt));
            if (cell) {
                const start = i;
                i += cellAt + cell.length;
                if (sheetMatch?.[1])
                    cell.addr.start.sheet = sheetMatch[1];
                tokens.push({ kind: "ref", span: { start, end: i }, addr: cell.addr });
                continue;
            }
            if (c === "$")
                throw new Error(`Unexpected character: ${c}`);
            const start = i;
            while (i < src.length && /[A-Za-z]/.test(src[i]))
                i++;
            const word = src.slice(start, i);
            const upper = word.toUpperCase();
            if (upper === "TRUE" || upper === "FALSE") {
                tokens.push({ kind: "bool", span: { start, end: i }, value: upper === "TRUE" });
                continue;
            }
            if (src[i] === "(") {
                tokens.push({ kind: "func", span: { start, end: i }, name: word });
                continue;
            }
            throw new Error(`Unexpected token: ${word}`);
        }
        throw new Error(`Unexpected character: ${c}`);
    }
    return tokens;
}
const PREFIX_BP = 8;
function infixBp(op) {
    switch (op) {
        case "=":
        case "<>":
        case "<":
        case ">":
        case "<=":
        case ">=":
            return { left: 1, right: 2 };
        case "+":
        case "-":
            return { left: 3, right: 4 };
        case "*":
        case "/":
            return { left: 5, right: 6 };
        case "^":
            return { left: 7, right: 7 };
        default:
            return null;
    }
}
function merge(a, b) {
    return { start: a.start, end: b.end };
}
export function parse(formula) {
    const tokens = tokenize(formula);
    let pos = 0;
    const parseExpr = (minBp) => {
        let left = parsePrefix();
        while (true) {
            const t = tokens[pos];
            if (!t || t.kind !== "op")
                break;
            const bp = infixBp(t.value);
            if (!bp || bp.left < minBp)
                break;
            pos++;
            const right = parseExpr(bp.right);
            left = { kind: "binary", op: t.value, span: merge(left.span, right.span), left, right };
        }
        return left;
    };
    const parsePrefix = () => {
        const t = tokens[pos];
        if (t?.kind === "op" && (t.value === "-" || t.value === "+")) {
            pos++;
            const operand = parseExpr(PREFIX_BP);
            return t.value === "-" ? { kind: "unary", op: "-", span: merge(t.span, operand.span), operand } : operand;
        }
        return parsePrimary();
    };
    const parsePrimary = () => {
        const t = tokens[pos++];
        if (!t)
            throw new Error("Unexpected end of formula");
        if (t.kind === "lparen") {
            const inner = parseExpr(0);
            const close = tokens[pos++];
            if (close?.kind !== "rparen")
                throw new Error("Expected )");
            return { ...inner, span: merge(t.span, close.span) };
        }
        if (t.kind === "func") {
            const open = tokens[pos++];
            if (open?.kind !== "lparen")
                throw new Error("Expected (");
            const args = [];
            if (tokens[pos]?.kind !== "rparen") {
                args.push(parseExpr(0));
                while (tokens[pos]?.kind === "comma") {
                    pos++;
                    args.push(parseExpr(0));
                }
            }
            const close = tokens[pos++];
            if (close?.kind !== "rparen")
                throw new Error("Expected )");
            return { kind: "call", name: t.name, span: merge(t.span, close.span), args };
        }
        if (t.kind === "num" || t.kind === "str" || t.kind === "bool" || t.kind === "err" || t.kind === "ref") {
            return { ...t };
        }
        throw new Error("Unexpected token");
    };
    const result = parseExpr(0);
    if (pos < tokens.length)
        throw new Error("Unexpected trailing input");
    return result;
}
//# sourceMappingURL=parser.js.map